import type { Input } from '../engine/Input';
import { moveAndCollide, aabbOverlap, type AABB } from '../engine/Physics';
import type { GameConfig } from '../types';

const TERMINAL_VY = 18;
const WALL_SLIDE_SPEED = 1.5;
const WALL_JUMP_VX = 10;
const WALL_STICK_FRAMES = 8;
const WALL_JUMP_COOLDOWN = 14;
const SHOOT_INTERVAL_MS = 200;
const PLAYER_BULLET_SPEED = 12;
const MAGAZINE_SIZE = 5;
const RELOAD_TIME_MS = 2000;
const MAX_HP = 5;

export interface BulletSpawn {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export class Player {
  pos = { x: 0, y: 0 };
  prevPos = { x: 0, y: 0 };
  vel = { x: 0, y: 0 };
  width: number;
  height: number;

  onGround = false;
  jumpsLeft = 2;
  facing: 1 | -1 = 1;
  alive = true;

  // -1 = левая стена, 0 = нет, 1 = правая стена
  onWall: -1 | 0 | 1 = 0;
  wallStickTimer = 0;
  wallJumpCooldown = 0;

  private shootCooldownMs = 0;
  ammo = MAGAZINE_SIZE;
  reloading = false;
  reloadTimeMs = 0;
  hp = MAX_HP;
  invincible = 0;
  carryingFlag = false;
  flyMode = false; // несёт флаг — штраф скорости/прыжка

  constructor(private cfg: GameConfig) {
    this.width = cfg.playerWidth;
    this.height = cfg.playerHeight;
  }

  spawnAt(x: number, y: number): void {
    this.pos.x = x;
    this.pos.y = y;
    this.prevPos.x = x;
    this.prevPos.y = y;
    this.vel.x = 0;
    this.vel.y = 0;
    this.onGround = false;
    this.onWall = 0;
    this.wallStickTimer = 0;
    this.wallJumpCooldown = 0;
    this.jumpsLeft = 2;
    this.alive = true;
    this.shootCooldownMs = 0;
    this.ammo = MAGAZINE_SIZE;
    this.reloading = false;
    this.reloadTimeMs = 0;
    this.hp = MAX_HP;
    this.invincible = 90;
  }

  bounds(): AABB {
    return { x: this.pos.x, y: this.pos.y, w: this.width, h: this.height };
  }

  tryShoot(input: Input, stepDtSec: number, worldMouseX?: number, worldMouseY?: number): BulletSpawn | null {
    if (!this.alive) return null;
    const dtMs = stepDtSec * 1000;

    // перезарядка по ПКМ
    if (input.wasRmbPressed() && !this.reloading && this.ammo < MAGAZINE_SIZE) {
      this.reloading = true;
      this.reloadTimeMs = RELOAD_TIME_MS;
    }

    // тик перезарядки
    if (this.reloading) {
      this.reloadTimeMs -= dtMs;
      if (this.reloadTimeMs <= 0) {
        this.ammo = MAGAZINE_SIZE;
        this.reloading = false;
        this.reloadTimeMs = 0;
      }
      return null;
    }

    // cooldown между выстрелами
    this.shootCooldownMs -= dtMs;
    if (this.shootCooldownMs > 0) return null;
    if (!input.isDown('shoot')) return null;
    if (this.ammo <= 0) return null;

    this.shootCooldownMs = SHOOT_INTERVAL_MS;
    this.ammo -= 1;

    // автоперезарядка после последнего патрона
    if (this.ammo === 0) {
      this.reloading = true;
      this.reloadTimeMs = RELOAD_TIME_MS;
    }

    const muzzleX = this.pos.x + this.width / 2 + this.facing * (this.width / 2 + 4);
    const muzzleY = this.pos.y + this.height * 0.6; // дуло примерно на 60% высоты персонажа
    let vx = this.facing * PLAYER_BULLET_SPEED;
    let vy = 0;
    if (worldMouseX !== undefined && worldMouseY !== undefined) {
      const dx = worldMouseX - muzzleX;
      const dy = worldMouseY - muzzleY;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > 0) {
        vx = (dx / len) * PLAYER_BULLET_SPEED;
        vy = (dy / len) * PLAYER_BULLET_SPEED;
      }
    }
    return { x: muzzleX, y: muzzleY, vx, vy };
  }

  private touchesWall(solids: readonly AABB[]): -1 | 0 | 1 {
    if (this.wallJumpCooldown > 0) return 0;
    const probeL: AABB = { x: this.pos.x - 4, y: this.pos.y + 2, w: 4, h: this.height - 4 };
    const probeR: AABB = { x: this.pos.x + this.width, y: this.pos.y + 2, w: 4, h: this.height - 4 };
    if (solids.some(s => aabbOverlap(probeL, s))) return -1;
    if (solids.some(s => aabbOverlap(probeR, s))) return 1;
    return 0;
  }

  step(input: Input, solids: readonly AABB[]): void {
    if (!this.alive) return;
    if (this.invincible > 0) this.invincible -= 1;

    // toggle fly mode
    if (input.wasPressed('fly')) this.flyMode = !this.flyMode;

    if (this.flyMode) {
      let vx = 0, vy = 0;
      if (input.isDown('left'))  vx = -this.cfg.playerSpeed * 2;
      if (input.isDown('right')) vx =  this.cfg.playerSpeed * 2;
      if (input.isDown('jump'))  vy = -this.cfg.playerSpeed * 2;
      if (input.isDown('shoot') && !input.isDown('left') && !input.isDown('right')) vy = this.cfg.playerSpeed * 2;
      this.vel.x = vx;
      this.vel.y = vy;
      this.pos.x += vx;
      this.pos.y += vy;
      this.onGround = false;
      return;
    }

    this.prevPos.x = this.pos.x;
    this.prevPos.y = this.pos.y;

    if (this.wallJumpCooldown > 0) this.wallJumpCooldown -= 1;

    let move = 0;
    if (input.isDown('left')) move -= 1;
    if (input.isDown('right')) move += 1;
    // facing меняется только по прицелу (мыши), не по движению — убирает дёрганье спрайта

    const speedMult = 1; // штраф за флаг убран
    // используем onWall с прошлого фрейма — он уже точный
    const stuckToWall = this.onWall !== 0 && !this.onGround;

    // прыжок
    if (input.wasPressed('jump')) {
      if (stuckToWall) {
        // wall jump — всегда в противоположную сторону от стены
        this.vel.x = -this.onWall * WALL_JUMP_VX;
        this.vel.y = this.cfg.jumpForce;
        this.jumpsLeft = 1;
        this.wallJumpCooldown = WALL_JUMP_COOLDOWN;
        this.wallStickTimer = 0;
        this.onWall = 0;
      } else if (this.jumpsLeft > 0) {
        this.vel.y = (this.jumpsLeft === 2 ? this.cfg.jumpForce : this.cfg.doubleJumpForce) * speedMult;
        this.jumpsLeft -= 1;
        this.onGround = false;
      }
    }

    // горизонтальное движение
    if (stuckToWall && this.wallJumpCooldown === 0) {
      // прилипший — не двигаемся горизонтально
      this.vel.x = 0;
    } else if (this.wallJumpCooldown > 0) {
      // после wall jump — разрешаем движение только AWAY от стены, не к ней
      // vel.x < 0 = летим влево, onWall перед прыжком был 1 (правая стена) — не пускаем вправо
      const wallDir = this.vel.x < 0 ? -1 : 1; // направление полёта после прыжка
      if (move !== 0 && move === wallDir) {
        // движение в сторону полёта — разрешаем
        const target = move * this.cfg.playerSpeed;
        this.vel.x = this.vel.x + (target - this.vel.x) * this.cfg.airControl;
      }
      // движение к стене — блокируем
    } else if (this.onGround) {
      this.vel.x = move * this.cfg.playerSpeed * speedMult;
    } else {
      const target = move * this.cfg.playerSpeed;
      this.vel.x = this.vel.x + (target - this.vel.x) * this.cfg.airControl;
    }

    // гравитация
    this.vel.y += this.cfg.gravity;

    // wall slide
    if (stuckToWall && this.vel.y > 0) {
      if (this.wallStickTimer < WALL_STICK_FRAMES) {
        this.vel.y = 0;
        this.wallStickTimer += 1;
      } else {
        this.vel.y = Math.min(this.vel.y, WALL_SLIDE_SPEED);
      }
    } else if (!stuckToWall) {
      this.wallStickTimer = 0;
    }

    if (this.vel.y > TERMINAL_VY) this.vel.y = TERMINAL_VY;

    const box = this.bounds();
    const res = moveAndCollide(box, this.vel.x, this.vel.y, solids);
    this.pos.x = box.x;
    this.pos.y = box.y;
    if (res.hitX) this.vel.x = 0; // всегда обнуляем при коллизии

    // обновляем onWall ПОСЛЕ движения — будет актуален в следующем фрейме
    if (!this.onGround) {
      this.onWall = this.touchesWall(solids);
    } else {
      this.onWall = 0;
    }

    if (res.hitY) {
      const wasFalling = this.vel.y > 0;
      this.vel.y = 0;
      if (wasFalling) {
        this.onGround = true;
        this.jumpsLeft = 2;
        this.wallStickTimer = 0;
      }
    } else {
      this.onGround = false;
    }
  }

  kill(): void {
    this.alive = false;
    this.vel.x = 0;
    this.vel.y = 0;
  }
}
